import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePhase } from '../../../contexts/PhaseContext';
import { getCurrentAcademicYearId } from '../../../utils/academicYearContext';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import {
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  ManageAccounts as ManageIcon,
  People as PeopleIcon
} from '@mui/icons-material';

const HubCard = ({ title, description, icon, actionText, onClick }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Button variant="contained" onClick={onClick}>{actionText}</Button>
    </CardActions>
  </Card>
);

export default function ClassManagementPage() {
  const navigate = useNavigate();
  const { activePhase, getPhaseLabel } = usePhase();
  const { t } = useTranslation();
  const ayId = getCurrentAcademicYearId();
  const reqSeq = useRef(0);
  const prevCtxRef = useRef('');

  const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
  const isSecPhase = resolvedPhaseCode === 'sec';
  const noClassMasterTitle = isSecPhase
    ? t('classManagement.overview.noClassMaster', { defaultValue: 'No Class Master' })
    : t('classManagement.overview.noClassTeacher', { defaultValue: 'No Class Teacher' });
  const noClassMasterTooltip = isSecPhase
    ? t('classManagement.overview.noClassMasterTooltip', { defaultValue: 'Classes without a class master assigned' })
    : t('classManagement.overview.noClassTeacherTooltip', { defaultValue: 'Classes without a class teacher assigned' });

  // Server-driven snippets (resilient to missing endpoints)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [recentClasses, setRecentClasses] = useState(null); // null until loaded
  const [recentLoading, setRecentLoading] = useState(true);
  const [extraMetrics, setExtraMetrics] = useState({
    activeClasses: 0,
    inactiveClasses: 0,
    noClassMaster: 0,
  });

  const loadData = useCallback(async () => {
    const mySeq = ++reqSeq.current;
    const ctrl = new AbortController();
    // Helper to enforce a timeout per request
    const withTimeout = async (p, ms = 5000) => {
      let to;
      const timeout = new Promise((_, rej) => {
        to = setTimeout(() => {
          rej(new Error('Request timed out'));
        }, ms);
      });
      try {
        const res = await Promise.race([p, timeout]);
        return res;
      } finally {
        clearTimeout(to);
      }
    };
    setLoading(true);
    setError('');
    try {
      // Fetch summary + recent in parallel (use confirmed-fast top-level endpoints via absolute backend URL)
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '1';
      // ---------- Per-context cache helpers (no-flash UI) ----------
      const ctxKey = `${schoolId}|${activePhase || '-' }|${ayId || '-'}`;
      const sumCacheKey = `classMgmt:summary:${ctxKey}`;
      const recCacheKey = `classMgmt:recent:${ctxKey}`;
      try {
        // If context changed, and there is no cache for the new context, clear UI immediately
        if (prevCtxRef.current !== ctxKey) {
          prevCtxRef.current = ctxKey;
          const hasSumCache = !!localStorage.getItem(sumCacheKey);
          const hasRecCache = !!localStorage.getItem(recCacheKey);
          if (!hasSumCache) setSummary(null);
          if (!hasRecCache) setRecentClasses(null);
        }
        // Pre-fill from cache so UI doesn't flash to zero when available
        const cachedSumRaw = localStorage.getItem(sumCacheKey);
        if (cachedSumRaw) {
          const cachedSum = JSON.parse(cachedSumRaw);
          if (cachedSum && typeof cachedSum === 'object') {
            if (reqSeq.current === mySeq) {
              setSummary(cachedSum);
              if (typeof window !== 'undefined') window.__classSummary = cachedSum;
            }
          }
        }
        const cachedRecRaw = localStorage.getItem(recCacheKey);
        if (cachedRecRaw) {
          const cachedRec = JSON.parse(cachedRecRaw);
          if (Array.isArray(cachedRec) && reqSeq.current === mySeq) setRecentClasses(cachedRec);
        }
      } catch (_) {}
      // ---------- Resolve effective phaseId (prefer id over code to avoid mismatches) ----------
      let phaseIdStored = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || localStorage.getItem('phaseId');
      if ((!phaseIdStored || phaseIdStored === 'null' || phaseIdStored === 'undefined') && activePhase) {
        try {
          const raw = localStorage.getItem('phaseCatalog');
          const arr = raw ? JSON.parse(raw) : [];
          if (Array.isArray(arr) && arr.length) {
            const found = arr.find(p => (p.code || '').toLowerCase() === String(activePhase).toLowerCase());
            if (found && found.id) phaseIdStored = String(found.id);
          }
        } catch (_) {}
        // As a last resort, do a quick fetch to resolve id synchronously
        if (!phaseIdStored && activePhase) {
          try {
            const q = await withTimeout(fetchJson('/api/phases', { skipAuth: true }), 1200);
            const arr = Array.isArray(q?.phases) ? q.phases : [];
            const found = arr.find(p => (p.code || '').toLowerCase() === String(activePhase).toLowerCase());
            if (found && found.id) {
              phaseIdStored = String(found.id);
              try { localStorage.setItem('schoolAdmin_activePhaseId', phaseIdStored); } catch (_) {}
            }
          } catch (_) {}
        }
      }
      const phaseParam = activePhase ? `&phase=${encodeURIComponent(activePhase)}` : '';
      const phaseIdParam = phaseIdStored ? `&phaseId=${encodeURIComponent(phaseIdStored)}` : '';
      const ayParam = ayId ? `&ayId=${encodeURIComponent(ayId)}` : '';
      const summaryUrlTop = `/api/classes/summary-public-top?schoolId=${encodeURIComponent(schoolId)}${phaseIdParam || phaseParam}${ayParam}`;
      const recentUrlTop = `/api/classes/recent-top-direct?schoolId=${encodeURIComponent(schoolId)}&limit=8${phaseIdParam || phaseParam}`;
      const classesLiteUrl = `/api/classes-lite?schoolId=${encodeURIComponent(schoolId)}${phaseParam}`;
      const [sumRes, liteRes] = await Promise.all([
        withTimeout(fetchJson(summaryUrlTop, { signal: ctrl.signal, skipAuth: true }).catch(() => null)),
        withTimeout(fetchJson(classesLiteUrl, { signal: ctrl.signal, skipAuth: true }).catch(() => null))
      ]);
      let s = sumRes;
      // If missing or zeros, retry once without credentials and with cache-busting
      const isZeroish = !s || ((Number(s.totalClasses || 0) === 0) && (Number(s.totalStudents || 0) === 0));
      if (isZeroish) {
        try {
          const bustUrl = `${summaryUrlTop}${summaryUrlTop.includes('?') ? '&' : '?'}_=${Date.now()}`;
          const j = await withTimeout(fetchJson(bustUrl, { skipAuth: true }).catch(() => null));
          if (j && (Number(j.totalClasses || 0) > 0 || Number(j.totalStudents || 0) > 0)) {
            s = j;
          }
        } catch (_) {}
      }
      if (s && typeof s === 'object' && reqSeq.current === mySeq) {
        setSummary(s);
        try { localStorage.setItem(sumCacheKey, JSON.stringify(s)); } catch (_) {}
      }
      // Compute extra metrics from classes-lite
      try {
        const classesArr = Array.isArray(liteRes?.classes) ? liteRes.classes : (Array.isArray(liteRes) ? liteRes : []);
        if (reqSeq.current === mySeq && Array.isArray(classesArr)) {
          let active = 0, inactive = 0, noMaster = 0;
          classesArr.forEach(c => {
            const isActive = c?.isActive !== false;
            if (isActive) active++; else inactive++;
            if (!c?.classMasterId) noMaster++;
          });
          setExtraMetrics({
            activeClasses: active,
            inactiveClasses: inactive,
            noClassMaster: noMaster,
          });
        }
      } catch (_) {}
      // Load recent classes (non-blocking) — strict phase-only
      try {
        setRecentLoading(true);
        const recent = await withTimeout(fetchJson(recentUrlTop, { skipAuth: true }).catch(() => ({ classes: [] })));
        const arr = Array.isArray(recent?.classes) ? recent.classes : [];
        if (reqSeq.current === mySeq) {
          setRecentClasses(arr);
          try { localStorage.setItem(recCacheKey, JSON.stringify(arr)); } catch (_) {}
        }
      } catch (_) {
        // Keep previous recent classes on failure to avoid flicker
      } finally {
        if (reqSeq.current === mySeq) setRecentLoading(false);
      }
    } catch (e) {
      if (e?.name !== 'AbortError' && reqSeq.current === mySeq) setError(e?.message || 'Failed to load class management data');
    } finally {
      if (reqSeq.current === mySeq) setLoading(false);
    }
    return () => { try { ctrl.abort(); } catch (_) {} };
  }, [activePhase, ayId]);

  // Auto-reload on mount and whenever PHASE or AY changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhase, ayId]);

  // Auto-refresh when phase or academic year changes
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    // Also listen to storage changes (in case Phase/AY set via localStorage elsewhere)
    const storageHandler = (e) => {
      try {
        if (!e) return;
        const k = e.key || '';
        // React to phase changes (metadata) and AY changes (enrollment counts)
        if (k === 'schoolAdmin_activePhase' || k.endsWith(':currentAcademicYearId') || k === 'currentAcademicYearId') {
          loadData();
        }
      } catch (_) {}
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, [loadData]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('classManagement.pageTitle') || 'Class Management'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {activePhase ? (
            null
          ) : null}
        </Box>
      </Box>
      {/* Intentionally suppress error banner to avoid noisy UI; stats render zeros until data arrives */}

      <Grid container spacing={3}>
        {/* Stats */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('classManagement.overview.title') || 'Overview'}
              </Typography>
              {(() => { const displaySummary = summary && typeof summary === 'object' ? summary : { totalClasses: 0, assignedClasses: 0, unassignedClasses: 0, totalStudents: 0, avgStudentsPerClass: 0 }; return (
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.totalClasses') || 'Total Classes'}
                    value={Number(displaySummary.totalClasses || 0)}
                    color="primary"
                    icon={<ClassIcon fontSize="large" color="primary" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.assignedClasses') || 'Assigned'}
                    value={Number(displaySummary.assignedClasses || 0)}
                    color="secondary"
                    icon={<AssignmentIcon fontSize="large" color="secondary" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.unassignedClasses') || 'Unassigned'}
                    value={Number(displaySummary.unassignedClasses || 0)}
                    color="warning"
                    icon={<AssignmentIcon fontSize="large" color="warning" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.totalStudents') || 'Total Students'}
                    value={Number(displaySummary.totalStudents || 0)}
                    color="success"
                    icon={<PeopleIcon fontSize="large" color="success" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.avgPerClass') || 'Avg/Class'}
                    value={Number(displaySummary.avgStudentsPerClass || 0)}
                    color="info"
                    icon={<AssessmentIcon fontSize="large" color="info" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.activeClasses') || 'Active Classes'}
                    value={Number(extraMetrics.activeClasses || 0)}
                    color="success"
                    icon={<ClassIcon fontSize="large" color="success" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stat
                    title={t('classManagement.overview.inactiveClasses') || 'Inactive Classes'}
                    value={Number(extraMetrics.inactiveClasses || 0)}
                    color="default"
                    icon={<ClassIcon fontSize="large" color="disabled" />}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Tooltip
                    title={noClassMasterTooltip}
                  >
                    <Box>
                      <Stat
                        title={noClassMasterTitle}
                        value={Number(extraMetrics.noClassMaster || 0)}
                        color="warning"
                        icon={<AssignmentIcon fontSize="large" color="warning" />}
                      />
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
              ); })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('classManagement.quickActions.title') || 'Quick Actions'}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <List dense>
                <ListItem button onClick={() => navigate('/class-management/add-class')}>
                  <ManageIcon sx={{ mr: 1 }} />
                  <ListItemText
                    primary={t('classManagement.quickActions.addClass.title') || 'Add New Class'}
                    secondary={t('classManagement.quickActions.addClass.subtitle') || 'Create a class in the current phase/year'}
                  />
                </ListItem>
                <ListItem button onClick={() => navigate('/class-management/manage-classes')}>
                  <ClassIcon sx={{ mr: 1 }} />
                  <ListItemText
                    primary={t('classManagement.quickActions.manageClasses.title') || 'Manage Classes'}
                    secondary={t('classManagement.quickActions.manageClasses.subtitle') || 'Edit class settings'}
                  />
                </ListItem>
                <ListItem button onClick={() => navigate('/class-management/class-assignments')}>
                  <AssignmentIcon sx={{ mr: 1 }} />
                  <ListItemText
                    primary={t('classManagement.quickActions.classAssignments.title') || 'Class Assignments'}
                    secondary={t('classManagement.quickActions.classAssignments.subtitle') || 'Teachers and students'}
                  />
                </ListItem>
                <ListItem button onClick={() => navigate('/class-management/class-reports')}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  <ListItemText
                    primary={t('classManagement.quickActions.classReports.title') || 'Class Reports'}
                    secondary={t('classManagement.quickActions.classReports.subtitle') || 'Enrollment & performance'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
                <Typography variant="h6">
                  {t('classManagement.recent.title') || 'Recent Classes'}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/class-management/manage-classes')}
                >
                  {t('classManagement.recent.viewAll') || 'View All'}
                </Button>
              </Box>
              {recentLoading ? (
                <Box sx={{ py: 2 }}>
                  <AppLoader variant="circular3" size={28} centered={false} minHeight="auto" />
                </Box>
              ) : !recentClasses || recentClasses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('classManagement.recent.empty') || 'No recent classes'}
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {recentClasses.map((c, idx) => (
                    <Grid item key={idx} xs={12} md={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {c?.name
                              || c?.className
                              || (t('classManagement.recent.cardFallbackName') || 'Class')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c?.level || c?.grade || '—'} · {c?.stream || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('classManagement.recent.students', {
                              count: c?.studentCount ?? c?.count ?? '—',
                              defaultValue: 'Students: {{count}}',
                            })}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                          <Button size="small" onClick={() => navigate('/class-management/manage-classes')}>
                            {t('classManagement.recent.open') || 'Open'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Hub tiles */}
        <Grid item xs={12} md={6} lg={3}>
          <HubCard
            title={t('classManagement.hub.manageClasses.title') || 'Manage Classes'}
            description={t('classManagement.hub.manageClasses.description') || 'Create, edit, and organize classes for the selected academic year and phase.'}
            icon={<ClassIcon />}
            actionText={t('classManagement.hub.manageClasses.action') || 'Open'}
            onClick={() => navigate('/class-management/manage-classes')}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <HubCard
            title={t('classManagement.hub.classAssignments.title') || 'Class Assignments'}
            description={t('classManagement.hub.classAssignments.description') || 'Assign teachers and students to classes and configure responsibilities.'}
            icon={<AssignmentIcon />}
            actionText={t('classManagement.hub.classAssignments.action') || 'Open'}
            onClick={() => navigate('/class-management/class-assignments')}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <HubCard
            title={t('classManagement.hub.classReports.title') || 'Class Reports'}
            description={t('classManagement.hub.classReports.description') || 'View enrollment and performance summaries by class.'}
            icon={<AssessmentIcon />}
            actionText={t('classManagement.hub.classReports.action') || 'Open'}
            onClick={() => navigate('/class-management/class-reports')}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <HubCard
            title={t('classManagement.hub.addClass.title') || 'Add Class'}
            description={t('classManagement.hub.addClass.description') || 'Quickly add a new class with phase/year context applied.'}
            icon={<AddIcon />}
            actionText={t('classManagement.hub.addClass.action') || 'Add'}
            onClick={() => navigate('/class-management/add-class')}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function Stat({ title, value, color }) {
  const formatted = typeof value === 'number' ? (Number.isInteger(value) ? value.toLocaleString() : Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })) : (value ?? '0');
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="h6" sx={{ letterSpacing: 0.2, fontWeight: 500 }} color="text.primary">{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }} color={`${color}.main`}>{formatted}</Typography>
      </CardContent>
    </Card>
  );
}